<template>
  <div id="payment-page">
    <loading-overlay
      :active.sync="displayLoader"
      loader="dots"
      color="#1A74DB"
      height="30"
      width="100%"
      opacity="0.85"
    ></loading-overlay>
    <div v-if="pageDataFetched">
      <checkout-header
        :categoryFilter="{ display: false }"
        :shoppingBag="{ display: false }"
      >
      </checkout-header>
      <div id="body">
        <div class="row block">
          <div class="col xs-12" style="padding: 0;">
            <div v-if="!isPmUpdate">
              <notice v-if="disclaimer" noticeType="notice">
                <template v-slot:content>
                  {{ disclaimer }}
                </template>
              </notice>
              <notice v-if="isDeferred && !isSoftwareCartEmpty" noticeType="notice">
                <template v-slot:content>
                  {{ paymentNotice }}
                </template>
              </notice>
              <notice v-if="isUpfrontPayment && !isSoftwareCartEmpty" noticeType="notice">
                <template v-slot:content>
                  {{ checkoutContent.upfrontPaymentNotice }}
                </template>
              </notice>
            </div>

            <h3 class="margin-top-50">
              {{ checkoutSharedContent.paymentDetails }}
            </h3>
            <p class="black bold" id="ssl-notice">
              <span class="icon small inline lock"></span>&nbsp;{{
                paymentMethod === "ACH"
                  ? "Banking data encrypted and secure"
                  : checkoutSharedContent.sslEncryption
              }}
            </p>
          </div>
        </div>

        <template v-if="paymentMethod === 'Invoice'">
          <invoice-info
            :newBillingProcessApproved="newBillingProcessApproved"
          ></invoice-info>
        </template>
        <template
          class="row margin-top-20"
          v-else-if="paymentMethod !== 'Invoice'"
        >
        <div v-if="defaultPaymentMethod">
          <div class="default-card">
            <div>
              <span class="default-payment-info">{{ checkoutSharedContent.enterPaymentInfo.defaultPaymentMethod }}:</span> 
              <span class="default-payment-info">{{ paymentMethodName }} ****{{ defaultPaymentMethod.last4 }}</span>
              <span v-if="defaultPaymentMethod.expMonth && defaultPaymentMethod.expYear" class="default_payment_expiration" >Exp {{ expirationMonth }}/{{ defaultPaymentMethod.expYear }}</span>
            </div>
          </div>
          <p class="default_note">{{ checkoutSharedContent.enterPaymentInfo.defaultPaymentNote }}</p>
        </div>
        <div>
          <h5
              style="margin-top: 40px"
              v-if="enterPaymentInfoHeader && !showAchMandate"
            >
              {{ enterPaymentInfoHeader }}
            </h5>
        </div>
          <div class="border border-[#A9B7C0] rounded-[3px] mb-[1.5rem]">
            <new-stripe-element
            :stripe="stripe"
            :stripeOptions="stripeOptions"
            @stripe-payment="handleStripeSubmit"
            @stripe-form-change="handleStripeFormChange"
            ></new-stripe-element>
            <div v-if="showUpdateDefaultPaymentMethod && !isPmUpdate" class="text-lg  flex align-middle px-[18px] py-[15px] mb-[15px]">
              <input type="checkbox" v-model="setAsDefault" class="!m-0 !mr-[15px] h-[30px] w-[30px] min-w-[30px]"/>
              <div class="my-auto"><b>{{ checkoutSharedContent.enterPaymentInfo.saveAsDefaultBold }}</b> {{ checkoutSharedContent.enterPaymentInfo.saveAsDefaultNotBold }}</div>
            </div>
          </div>
          <div v-if="hasCCFee">
            <p class="default_note">Note: {{ checkoutContent.ccAdditionalFeeNote }}</p>
          </div>
          <div class="cc-notice-wrapper" v-if="creditCardLimitExceeded">
            <strong>{{checkoutSharedContent.notices.please_note}} </strong
            >{{
              checkoutSharedContent.notices.credit_limit_exceeded
                .replace("__limit__", creditCardLimit[currencyIsoCode])
                .replace("__currencyCode__", currencyIsoCode)
                .replace("__email__", ownerEmail)
            }}
          </div>

          <div v-if="canSeeAutoRenewalExperiment && writtenLanguage === 'English' && autoRenewalOptOut && stripeOptions.customerId" class="text-lg border border-[#A9B7C0] flex align-middle px-[18px] py-[15px] rounded-[3px] mt-[37px] mb-[50px]">
            <input type="checkbox" v-model="hasOptedIntoAutoRenewal" class="!m-0 !mr-[15px] h-[30px] w-[30px] min-w-[30px]"/>
            <div class="my-auto"><b>{{ renewalsPersistedVariables.jsonContent.text.optIn }}</b> {{ renewalsPersistedVariables.jsonContent.text.autoRenew }}</div>
          </div>

          <div v-if="chargeImmediately || isENT" class="row text-center pt-1">
            <div class="col xs-12 text-2xl">
              {{ `${totalText}: ${currencySymbol}${calculateTotal}` }}
            </div>
          </div>
          <div
            v-if="paymentType === 'us_bank_account' && !showAchMandate"
            class="col xs-12 text-center"
          >
            <h3 class="mandate-h3 bold mb-0 pt-0.5">
              ACH privacy and Safety policies
            </h3>
            <a
              target="_blank"
              rel="noopener noreferrer"
              class="blue-text underline cursor-pointer"
              href="https://support.stripe.com/questions/who-can-access-data-from-my-linked-financial-account-and-for-what-purposes"
              >Data Privacy</a
            >,
            <a
              target="_blank"
              rel="noopener noreferrer"
              class="blue-text underline cursor-pointer"
              href="https://support.stripe.com/questions/who-will-obtain-my-financial-institution-login-credentials"
              >Login Protection</a
            >,
            <a
              target="_blank"
              rel="noopener noreferrer"
              class="blue-text underline cursor-pointer"
              href="https://support.stripe.com/questions/is-my-financial-account-information-safe"
              >Account Safety</a
            >
          </div>
        </template>

        <template class="margin-top-20" v-else>
          <div class="display-card">
            <p class="display-card-text">
              {{ achPaymentCompanyName }}
              <span class="display-card-icon" :class="isMicro && 'pending'">{{
                isMicro ? "Pending Verification" : "Linked &nbsp; &#10003;"
              }}</span>
            </p>
          </div>
          <div v-if="isMicro">
            <p class="bold dark-blue-text email-verification">
              Microdeposit verification email will be sent to: {{ email }}
            </p>
          </div>
        </template>

        <div v-if="paymentSubmissionError" class="row">
          <div class="col xs-12 small-text red-text" id="errors">
            {{ paymentSubmissionError }}
          </div>
        </div>

        <div
          v-if="paymentMethod === 'BACS'"
          class="margin-top-20 margin-bottom-100"
        >
          <div id="body">
            <div class="row">
              <div
                class="col xs-12"
                v-html="checkoutSharedContent.bacsNotice"
              ></div>
            </div>
            <div
              id="contact-information"
              v-html="checkoutSharedContent.samsaraContactInformation"
            ></div>
          </div>
        </div>

        <div
          v-if="paymentMethod === 'ACSS'"
          class="margin-top-50 margin-bottom-100"
        >
          <h3>{{ checkoutContent.legalNotices.acss_mandate_header }}</h3>
          <h5>
            {{ checkoutContent.legalNotices.date_of_acceptance }}:
            {{ currentDate }}
          </h5>
          <p>
            {{ checkoutContent.legalNotices.acss_mandate_body }}
          </p>
        </div>

        <div
          v-if="paymentMethod === 'ACH' && showAchMandate"
          class="margin-top-50 margin-bottom-50 mandate"
        >
          <h3 class="mandate-h3">
            By clicking "{{
              chargeImmediately
                ? isMicro
                  ? checkoutSharedContent.enterPaymentInfo.microButtonText
                  : checkoutSharedContent.makePayment
                : checkoutSharedContent.updatePaymentMethod
            }}", you authorize Samsara Inc. ("Samsara") to debit the bank
            account specified above for any amount owed for charges arising from
            your use of Samsara services and/or purchase of products from
            Samsara, pursuant to Samsara’s
            <a
              href="https://www.samsara.com/legal/platform-terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              style="text-decoration: underline"
              >terms of service</a
            >, until you revoke the authorization. You may amend or cancel this
            authorization at any time by providing 30 (thirty) days written
            notice to Samsara.
          </h3>
          <h5 class="mandate-h5">
            If you use Samsara's services or purchase additional Samsara
            products periodically pursuant to Samsara’s terms, you authorize
            Samsara to debit your bank account periodically. Payments that fall
            outside of the regular debits authorized above will only be debited
            after your authorization is obtained.
          </h5>
        </div>

        <div v-if="canceledInstantVerificationModal" class="row">
          <div class="col margin-top-20 xs-12 medium-text" id="errors">
            We see you've closed the Connect Account window. Are you looking to
            pay via Credit Card? If so click
            <a :click="routeToReview">here</a> and then select Credit Card from
            the drop down.
          </div>
        </div>

        <submit-button
          id="submit-button"
          position="center margin-top-20 margin-bottom-50"
          class="b-width"
          mobile="12"
          tablet="3"
          desktop="3"
          styling="primary"
          form="payment-form"
          v-if="!hideSubmitButton"
          :disabled="disableSubmitButton"
        >
          <template v-slot:content>
            {{ submitButtonText }}
          </template>
        </submit-button>

        <submit-button
          id="submit-button"
          position="center margin-top-20 margin-bottom-50"
          class="b-width"
          mobile="12"
          tablet="3"
          desktop="3"
          styling="primary"
          v-else-if="showInvoiceButton"
          :disabled="disableSubmitButton"
          @onClickEvent="submitPaymentHandler"
        >
          <template v-slot:content>
            {{ submitButtonText }}
          </template>
        </submit-button>

        <p class="change-bank" v-if="showAchMandate" @click="changeBankAccount">
          Change bank account
        </p>
      </div>
    </div>
    <MicroModal
      :isOpen="showMicroModal"
      @hide-modal="showMicroModal = false"
      @submit-payment-data="submitPaymentHandler"
      @route-to-review="routeToReview"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import axios from "axios";

// mixins
import { StripeMixin } from "../../../../mixins/stripe/stripe";
import ScrollToTop from "../../../../mixins/scrollToTop";
import { analyticsTrack } from "../../../../../bundled_js/analytics_wrapper";
import SalesforceResources from "../../../../mixins/salesforce-resources";
import SegmentResources from "../../../../mixins/segment-resources";
import renewalsResources from "../../../../mixins/renewals-resources";

// components
import CheckoutHeader from "../../layouts/header";
import Notice from "../../../utils/notice";
import NewStripeElement from "../../../utils/new-stripe-element";
import SubmitButton from "../../../utils/button";
import InvoiceInfo from "../PaymentMethods/invoice-info";
import MicroModal from "../micro-modal";

// form schemas
import achSchema from "../../../../forms/schemas/payment-methods/ach";
import bacsSchema from "../../../../forms/schemas/payment-methods/bacs";
import acssSchema from "../../../../forms/schemas/payment-methods/acss";

// utility helpers
import { camelize, getProperties } from "../../../../utils";

// credit card limits
import {
  currencyToPaymentMethods,
  countryCodeToCurrency,
  countryCodeMappings,
  stripeElementPaymentMethods,
  nonStripePaymentMethods,
  MidmarketCheckout,
  stripeToPaymentMethod,
  USD_CREDIT_CARD_LIMIT,
  EUR_CREDIT_CARD_LIMIT,
  GBP_CREDIT_CARD_LIMIT,
} from "../../../../settings";

const paymentMethodToSchema = {
  ACH: achSchema,
  BACS: bacsSchema,
  ACSS: acssSchema,
};

export default {
  mixins: [
    StripeMixin,
    ScrollToTop,
    SalesforceResources,
    SegmentResources,
    renewalsResources,
  ],

  components: {
    CheckoutHeader,
    Notice,
    SubmitButton,
    InvoiceInfo,
    MicroModal,
    NewStripeElement
  },

  data() {
    return {
      pageDataFetched: false,
      elementInputComplete: false,
      paymentSubmissionError: "",
      clientSecret: this.$store.state.ach.clientSecret || "",
      submissionInProgress: false,
      stripeElementPaymentMethods: stripeElementPaymentMethods,
      achPaymentCompanyName: false,
      showAchMandate: false,
      newBillingProcessApproved: false,
      microMessageShow: false,
      showMicroModal: false,
      ownerEmail: "",
      paymentType: "",
      ccFee: 1,
      paymentTotals: {
        total: undefined,
        subTotal: undefined,
        taxes: undefined,
      },
      isUserFormValid: false,
      stripeOptions: {
        account: undefined,
        clientSecret: undefined,
        publishableKey: undefined,
        customerId: undefined,
      },
      creditCardLimit: {
        USD: USD_CREDIT_CARD_LIMIT,
        EUR: EUR_CREDIT_CARD_LIMIT,
        GBP: GBP_CREDIT_CARD_LIMIT,
      },
      hasOptedIntoAutoRenewal: false,
      canSeeAutoRenewalExperiment: false,
      defaultPaymentMethod: null,
      setAsDefault: false,
      showUpdateDefaultPaymentMethod: false
    };
  },

    computed: {
        ...mapGetters([
            'currencySymbol',
            'total',
            'currentDate',
            'isPhoenixCustomer',
            'isPhoenixPhaseTwoCustomer',
            'applicationVersion',
            'typeOfRenewal',
            'productSource'
        ]),

        ...mapGetters("checkout", [
                    "paymentMethod",
                    "isDeferred",
                    "selectedPaymentType",
                    "isPmUpdate",
                    "accountId",
                    "opptyId",
                    "isRenewal",
                    "isMicro",
                    "licenseStartDate",
                    "initialPaymentTerm",
                    "isENT",
                    "checkoutType",
                    "currencyIsoCode",
                    "autoRenewalOptOut",
                    "writtenLanguage"
        ]),

        ...mapGetters("content", ["checkoutContent", "checkoutSharedContent", "renewalsPersistedVariables"]),

        ...mapGetters("shipping", ["firstName", "lastName", "email"]),

        ...mapGetters("billing", ["billing", "country"]),

        ...mapGetters("shoppingCart", [
        "productsInCart",
        "productCostsInfo",
        "isSoftwareCartEmpty",
        "consolidatedMmyData",
        "consolidatedVinData",
        "consolidatedManualSelectionData",
        ]),

    creditCardLimitExceeded() {
      return (
        !this.isPmUpdate &&
        this.paymentTotals.subTotal > (this.creditCardLimit?.[this.currencyIsoCode] || Infinity)
      );
    },

        paymentInfo() {
        return this.$store.getters[
            `${camelize(this.paymentMethod)}/${camelize(this.paymentMethod)}`
        ];
        },

        displayLoader() {
        return !this.pageDataFetched || this.submissionInProgress;
        },

        hideSubmitButton() {
        if (this.showAchMandate) {
            return false;
        } else {
            return !stripeElementPaymentMethods.includes(this.paymentMethod);
        }
        },

        showInvoiceButton() {
        return (
            nonStripePaymentMethods.includes(this.paymentMethod)
        );
        },

        disableLinkButton() {
        let paymentInfoSubmission = this.$refs.stripeElement
            ? this.$refs.stripeElement.paymentElement
            : this.paymentInfo;
        return paymentInfoSubmission.companyName === "";
        },

        paymentNotice() {
        if (this.isRenewal && this.isDeferred) {
            if (this.initialPaymentTerm === "Due on Contract Start") {
            return this.checkoutContent.deferredPaymentNotice.replace(
                "30",
                this.licenseStartDate
            );
            } else {
            return this.checkoutContent.deferredPaymentNotice.replace(
                "30",
                this.licenseStartDate + 30
            );
            }
        } else if (this.isDeferred) {
            return this.checkoutContent.deferredPaymentNotice.replace(
            "rep_email",
            this.ownerEmail
            );
        } else {
            return "";
        }
        },

        disclaimer() {
          let disclaimer =
            this.checkoutSharedContent.updatePaymentMethodDisclaimer.split("__/__");

          let disclaimerText = null;

          if((!this.isSoftwareCartEmpty && !this.defaultPaymentMethod) || (!this.stripeOptions.customerId && this.isFirstPurchase && !this.isSoftwareCartEmpty)){
            disclaimerText = disclaimer[0]
          }

          // Show the no payment method update text for ENT customers
          if (this.isENT && !this.isSoftwareCartEmpty) {
            disclaimerText = disclaimer[1];
          }
          return disclaimerText;
        },

        enterPaymentInfoHeader() {
          if(!this.paymentMethod){
            return ""
          }
          
          return this.checkoutSharedContent.enterPaymentInfo[
              this.paymentMethod.toLowerCase() + "Header"
              ] ?? ""
  
        },

        doesPaymentMethodHaveStripeElement() {
        return stripeElementPaymentMethods.includes(this.paymentMethod);
        },

        formPlaceholders() {
        if (
            stripeElementPaymentMethods.includes(this.paymentMethod) ||
            nonStripePaymentMethods.includes(this.paymentMethod)
        ) {
            return {};
        }
        let formPlaceholders = {};
        for (let group of this.paymentMethodSchema.groups) {
            for (let formInput of group.fields) {
            if (formInput.model in this.checkoutContent.formPlaceholders) {
                formPlaceholders[formInput.model] =
                this.checkoutContent.formPlaceholders[formInput.model];
            } else if (
                formInput.model in this.checkoutSharedContent.enterPaymentInfo
            ) {
                formPlaceholders[formInput.model] =
                this.checkoutSharedContent.enterPaymentInfo[formInput.model];
            } else {
                // pass
            }
            }
        }
        return formPlaceholders;
        },

        paymentMethodSchema() {
        return !stripeElementPaymentMethods.includes(this.paymentMethod)
            ? paymentMethodToSchema[this.paymentMethod]
            : {};
        },

        isUpfrontPayment() {
        return !this.isENT && this.selectedPaymentType === "Upfront Payments";
        },

        chargeImmediately() {
        return !this.isDeferred && !this.isPmUpdate;
        },

        submitButtonText() {
        if (this.isPmUpdate) {
            return this.checkoutSharedContent.updatePaymentMethod;
        } else if (this.paymentMethod === "Invoice") {
            return this.checkoutSharedContent.enterPaymentInfo.invoiceButtonText;
        } else {
            return this.checkoutSharedContent.makePayment;
        }
        },

        disableSubmitButton() {
        // is submission in process
        if (this.submissionInProgress) {
            return true;
        }

        // Invoices are ready to confirm right away
        if (this.paymentMethod === "Invoice") {
            return false;
        }

        // are we on the mandate step of an ACH payment (need to show this info)
        if (this.showAchMandate) {
            return false;
        }
        // has stripe element been completed with no errors
        if (
            stripeElementPaymentMethods.includes(this.paymentMethod) &&
            !this.elementInputComplete
        ) {
            return true;
        }

        return false;
        },

        hasCCFee() {
        return (
            this.paymentType === "card" &&
            this.checkoutType === MidmarketCheckout &&
            this.currencyIsoCode === "USD" &&
            !this.isENT
        );
        },

        totalText() {
        return this.hasCCFee
            ? this.checkoutSharedContent.totalCostText.totalWithTaxAndShippingAndCCFee
            : this.checkoutSharedContent.totalCostText.totalWithTaxAndShipping;
        },

        calculateTotal() {
        const { subTotal, taxes } = this.paymentTotals;

        const shipping_and_handling =
            this?.productCostsInfo?.additional_upfront_line_items
            ?.shipping_and_handling;

        const shipping_and_handling_tax =
            this?.productCostsInfo?.additional_upfront_line_items
            ?.shipping_and_handling_tax;

        if (
            this.checkIfValueIsNumber(subTotal) &&
            this.checkIfValueIsNumber(taxes) &&
            this.checkIfValueIsNumber(shipping_and_handling) &&
            this.checkIfValueIsNumber(shipping_and_handling_tax)
        ) {
            const maybeSubtotalWithFee = this.hasCCFee
            ? subTotal * this.ccFee
            : subTotal;

            const total =
            maybeSubtotalWithFee +
            taxes +
            shipping_and_handling +
            shipping_and_handling_tax;

            return total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            });
        }
        return "";
        },
        paymentMethodName (){
          
            switch(this?.defaultPaymentMethod?.type){
              case "card":
                return this.defaultPaymentMethod.brand.toUpperCase()
              case "us_bank_account":
              case "acss_debit":
                return this.defaultPaymentMethod.bankName.toUpperCase()
              case "sepa_debit":
              case "bacs_debit":
                return this.defaultPaymentMethod.type.toUpperCase().replaceAll("_", " ")
              default:
                return ""
            }
          },
          expirationMonth(){
            const expMonth = this?.defaultPaymentMethod?.expMonth
            if (expMonth){
              if (expMonth  < 10) {
                return String(expMonth).padStart(2, "0")
              }else {
                return expMonth
              }
            }
            return ""
          }
  },

  methods: {
    ...mapMutations(["setCurrencySymbol", "setTotal", "setCurrentDate"]),

    ...mapMutations("shipping", ["setFirstName", "setLastName", "setEmail"]),

    ...mapMutations("billing", ["setBilling"]),

    ...mapMutations("checkout", [
      "setPaymentMethod",
      "setIsENT",
      "setIsDeferred",
      "setSelectedPaymentType",
      "setIsPmUpdate",
      "setIsMicro",
      "setLastFour",
      "setAccountId",
      "setIsRenewal",
      "setLicenseStartDate",
      "setInitialPaymentTerm",
      "setPaymentMethod",
      "setAutoRenewalOptOut",
      "setWrittenLanguage",
    ]),

    ...mapActions("content", ["getCheckoutContent", "getRenewalsPersistedVariables"]),

    fireSuccessfulPurchaseSegmentTracking() {
      const productsList = this.productsInCart.map(
        getProperties("productKey", "offerName", "quantity")
      );
      const properties = {
        products: productsList,
        paymentLength: this.selectedPaymentType,
        paymentMethod: this.paymentMethod,
      };
      this.fireSegmentTrackingWithSfdcSegments(
        "Order Placed",
        this.sfdcSegments,
        properties
      );
    },

    changeBankAccount() {
      // Page refresh for ACH payments to re-enter business name and select account
      this.submissionInProgress = true;
      return window && window.location && window.location.reload();
    },

    handleStripeFormChange(stripeElementChangeEvent) {
      this.paymentType = stripeElementChangeEvent.value.type;
      this.setPaymentMethod(
        stripeToPaymentMethod?.[this.paymentType] ?? stripeToPaymentMethod.card
      );
      this.updateElementInputCompletionStatus(
        stripeElementChangeEvent.complete
      );
    },

    async handleStripeSubmit(stripeElementsObject) {
      this.submissionInProgress = true;
      const updatePaymentAndTotal =
        await this.updateCablesSelectionPriceAndPaymentMethod();

      if (updatePaymentAndTotal === "error") {
        this.paymentSubmissionError =
          "Something went wrong when updating the payment and cables.";

        this.submissionInProgress = false;
        return;
      }

      if (this.hasOptedIntoAutoRenewal) {
        await this.updateAutoRenewalOptInStatus();
      }
      if (this.setAsDefault){
        analyticsTrack("Default Payment Method Box Checked", {"accountId": this.accountId, "opptyId": this.opptyId, "version": this.applicationVersion, "source": this.productSource, "paymentMethod": this.paymentMethod});
      }
      analyticsTrack("Make Payment Button Clicked", {"accountId": this.accountId, "opptyId": this.opptyId, "version": this.applicationVersion, "phase": this.typeOfRenewal, "source": this.productSource});
      const paymentMethodData = {
        billing_details: {
          email: this.billing.email,
          name: `${this.billing.firstName} ${this.billing.lastName}`,
          address: {
            country: countryCodeMappings[this.billing.country],
            city: this.billing.city,
            line1: this.billing.address1,
            postal_code: this.billing?.zip,
          },
        },
      };
      if (this.chargeImmediately && updatePaymentAndTotal.status === 201) {
        this.handleImmediatePayment(stripeElementsObject, paymentMethodData);
      } else if (updatePaymentAndTotal.status === 201) {
        this.handleSetupPayment(stripeElementsObject, paymentMethodData);
      }
    },

    async updateAutoRenewalOptInStatus() {
      try {
        const response = await axios({
          method: "POST",
          url: "/checkout/update-auto-renewal-status",
          headers: { "Content-type": "application/json" },
          data: {
            hasOptedIntoAutoRenewal: this.hasOptedIntoAutoRenewal,
            analyticsId: this.getAnalyticsCookie(),
          },
          timeout: 30000,
        });
        return response;
      } catch (error) {
        console.error(error);
        return;
      }
    },

    async updateCablesSelectionPriceAndPaymentMethod() {
      try {
        const response = await axios({
          method: "POST",
          url: "/checkout/update-intent",
          headers: { "Content-type": "application/json" },
          data: {
            payment_method: this.paymentMethod,
            setAsDefault: this.setAsDefault,
            cable_selection: {
              vinData: this.consolidatedVinData,
              mmyData: this.consolidatedMmyData,
              manualSelectionData: this.consolidatedManualSelectionData,
            },
          },
          timeout: 30000,
        });
        return response;
      } catch (error) {
        console.error(error);
        return "error";
      }
    },

    async handleImmediatePayment(stripeElementsObject, paymentMethodData) {
      try {
        const { error } = await this.stripe.confirmPayment({
          elements: stripeElementsObject,
          confirmParams: {
            return_url: `${document.location.origin}/checkout/confirm-payment`,
            payment_method_data: paymentMethodData,
          },
        });
        this.handleStripeError(error);
      } catch (error) {
        console.error(error);
        this.handleStripeError(error);
      }
    },

    async handleSetupPayment(stripeElementsObject, paymentMethodData) {
      try {
        const { error } = await this.stripe.confirmSetup({
          elements: stripeElementsObject,
          confirmParams: {
            return_url: `${document.location.origin}/checkout/confirm-payment`,
            payment_method_data: paymentMethodData,
          },
        });
        this.handleStripeError(error);
      } catch (error) {
        this.handleStripeError(error);
      }
    },

    handleStripeError(error) {
      this.submissionInProgress = false;
      if (error?.type === "card_error" || error?.type === "validation_error") {
        this.paymentSubmissionError = error.message;
      } else if ("message" in error) {
        this.paymentSubmissionError = error.message;
      } else if (error) {
        this.paymentSubmissionError =
          this.checkoutContent.clientMessages.unable_to_checkout;
      }
    },

    buildFormModel() {
      if (this.paymentMethod === "ACSS" || this.paymentMethod === "BACS") {
        // eslint-disable-next-line no-unused-vars
        let { paymentMethodId, ...formModel } = this.paymentInfo;
        return formModel;
      }
      return { ...this.paymentInfo };
    },

    initializePaymentStoreData: async function (paymentPageData) {
      this.setCurrencySymbol(paymentPageData.data.currency_symbol);
      this.setIsENT(paymentPageData.data.is_ENT);
      this.setTotal(paymentPageData.data.total);
      this.setLastName(paymentPageData.data.last_name);
      this.setEmail(paymentPageData.data.email);
      this.setSelectedPaymentType(paymentPageData.data.selected_payment_type);
      this.setIsPmUpdate(paymentPageData.data.is_pm_update);
      this.setAccountId(paymentPageData.data.account_id);
      this.setIsRenewal(paymentPageData.data.is_renewal);
      this.setLicenseStartDate(paymentPageData.data.license_start_date);
      this.setInitialPaymentTerm(paymentPageData.data.initial_payment_term);
      this.setCurrentDate(paymentPageData.data.current_date);
      this.setBilling(paymentPageData.data.billing);
      this.setIsDeferred(paymentPageData.data.is_deferred);
      this.setAutoRenewalOptOut(paymentPageData.data.auto_renewal_opt_out);
      this.setWrittenLanguage(paymentPageData.data.written_language);
      this.ownerEmail = paymentPageData.data.owner_email;
      this.paymentTotals.total = paymentPageData.data.total;
      this.paymentTotals.subTotal = paymentPageData.data.sub_total;
      this.paymentTotals.taxes = paymentPageData.data.taxes_total;
      this.ccFee = paymentPageData.data.cc_fee;
      this.canSeeAutoRenewalExperiment = paymentPageData.data.can_see_auto_renewal_experiment;
      this.isFirstPurchase = paymentPageData.data.first_purchase
      if (
        paymentPageData.data.billing.country in countryCodeMappings &&
        currencyToPaymentMethods[
          countryCodeToCurrency[
            countryCodeMappings[paymentPageData.data.billing.country]
          ]
        ].includes(paymentPageData.data.payment_method)
      ) {
        this.setPaymentMethod(paymentPageData.data.payment_method);
      } else {
        this.setPaymentMethod(
          currencyToPaymentMethods[
            countryCodeToCurrency[
              countryCodeMappings[paymentPageData.data.billing.country]
            ]
          ]?.[0]
        );
      }
      if (paymentPageData.data.default_pm) {
        this.defaultPaymentMethod = paymentPageData.data.default_pm;
      }
  
      this.showUpdateDefaultPaymentMethod = paymentPageData.data.show_update_default_checkbox

      if (paymentPageData.data.payment_method) {
        analyticsTrack("Payment Initiated", {
          payment_method: this.paymentMethod,
        });
      }
      this.newBillingProcessApproved =
        paymentPageData.data.new_billing_process_approved;
      this.stripeOptions = paymentPageData.data.stripe;
      await this.getCheckoutContent();
      await this.getRenewalsPersistedVariables();
    },

    camelize(value) {
      return camelize(value);
    },
    checkIfValueIsNumber(value) {
      return typeof value === "number";
    },

    updateElementInputCompletionStatus(value) {
      this.elementInputComplete = value;
    },

    routeToReview() {
      this.submissionInProgress = true;
      this.$router.push({ path: "/checkout/review" });
    },

    setPaymentMethodStoreDataFromStripe(response) {
      let paymentMethodId =
        "paymentIntent" in response
          ? response.paymentIntent.payment_method
          : response.setupIntent.payment_method;
      this.$store.commit(
        `${camelize(this.paymentMethod)}/setPaymentMethodId`,
        paymentMethodId
      );
    },

    submitPaymentData: async function () {
      let response = await axios({
        method: "POST",
        url: "/checkout/submit-payment-data",
        headers: { "Content-type": "application/json" },
        data: {
          vinData: this.consolidatedVinData,
          mmyData: this.consolidatedMmyData,
          manualSelectionData: this.consolidatedManualSelectionData,
          ...this.paymentInfo
        },
        timeout: 30000,
      });
      return response;
    },

    setIsUserFomIsValid(isFormValid) {
      this.isUserFormValid = isFormValid ?? false;
    },

    intentSubmissionHandler: async function () {
      let paymentInfoSubmission = this.$refs.stripeElement
        ? this.$refs.stripeElement.paymentElement
        : this.paymentInfo;

      const intentInformation = {
        firstName: this.firstName,
        lastName: this.lastName,
        paymentMethod: this.paymentMethod,
        chargeImmediately: this.chargeImmediately,
        email: this.email,
        billingInfo: this.billing,
        clientSecret: this.stripeOptions.clientSecret,
        paymentInfoSubmission,
      };

      let intent = await this.createAndConfirmIntent(intentInformation);
      if (!intent || intent.error) {
        this.handleStripeJsError(intent);
        return false;
      }
      return intent;
    },

    submitPaymentHandler: async function () {
      this.submissionInProgress = true;
      this.paymentSubmissionError = null;
      // is session active ping
      let sessionStatus = null;
      try {
        sessionStatus = await axios({
          method: "POST",
          url: "/checkout/session-check",
          headers: { "Content-type": "application/json" },
          data: {
            page: "payment",
          },
          timeout: 20000,
        });
      } catch (e) {
        this.$router.push({
          path: "/message",
          query: { type: "errorCheckingOut" },
        });
        return;
      }
      // validate session status
      if (!sessionStatus.data.is_active) {
        this.$router.push({
          path: "/message",
          query: { type: "sessionExpired" },
        });
        return;
      }
      if (this.isMicro && this.microMessageShow === false) {
        this.showMicroModal = true;
        this.microMessageShow = true;
        this.submissionInProgress = false;
        return false;
      }
      // if payment method uses user-form component, validate form fields (except ACH bc the form is gone after linking account)
      if (
        !stripeElementPaymentMethods.includes(this.paymentMethod) &&
        !["ACH", "Invoice"].includes(this.paymentMethod)
      ) {
        let userFormValidationStatus = this.$refs.userForm.$refs.vfg.validate();
        if (!userFormValidationStatus) {
          this.submissionInProgress = false;
          return false;
        }
      }
      // if payment method is ACSS
      if (this.paymentMethod === "ACSS") {
        let response = await this.intentSubmissionHandler();
        if (!response) {
          this.submissionInProgress = false;
          return false;
        }
        this.setPaymentMethodStoreDataFromStripe(response);
      }

      let analytics = { payment_method: this.paymentMethod };

      analyticsTrack("Payment Completed", analytics);
      
      this.fireSuccessfulPurchaseSegmentTracking();

      // submit data to server, route client
      try {
          let paymentSubmission = await this.submitPaymentData();
          if (paymentSubmission.status === 200) {
              if(this.isPhoenixCustomer || this.isPhoenixPhaseTwoCustomer){
                analyticsTrack("Renewal Make Payment Button Clicked", {"accountId": this.accountId, "opptyId": this.opptyId, "version": this.applicationVersion, "phase": this.typeOfRenewal});
                  this.$router.push({path: "/checkout/confirm-renewal"})
              }
              else{
                  this.$router.push({path: "/checkout/confirm"});
              }
              return;
          } else {
              throw "unhandledStatusCode";
          }
      } catch(e) {
          if (e.response && e.response.status === 400 && e.response.data === "routing_number_invalid") {
              this.paymentSubmissionError = this.checkoutContent.clientMessages.invalid_routing_number.body;
              this.submissionInProgress = false;
              return false;
          }
          this.$router.push({path: '/message', query: {type: 'errorCheckingOut'}});
          return;
      }
    },
    handleStripeJsError(response) {
        if (response.error) {
            this.paymentSubmissionError = response.error.message;
        } else {
            this.paymentSubmissionError = this.checkoutContent.clientMessages.unable_to_checkout;
        }
    },
},

  async created() {
    if (
      "event" in this.$route.query &&
      this.$route.query.event !== "signing_complete"
    ) {
      this.$router.push({
        path: "/message",
        query: { type: "contractDeclined" },
      });
      return;
    }

    let paymentPageData = null;
    try {
      axios({
          method: 'POST',
          url: '/checkout/page-viewed-tracking',
          headers: {'Content-type': 'application/json'},
          data: {
              analyticsId: this.getAnalyticsCookie(),
              pageName: "Payment"
          }
      });
      paymentPageData = await axios("/checkout/payment-page-data");
      if (paymentPageData.status === 200) {
        await this.initializePaymentStoreData(paymentPageData);
      } else {
        throw "unhandledStatusCode";
      }
    } catch (e) {
      console.error(e);
      if (typeof e === "string") {
        this.$router.push({
          path: "/message",
          query: { type: "unableToUpdatePaymentMethod" },
        });
        return;
      }
      if (e.response?.data === "sessionExpired") {
        this.$router.push({
          path: "/message",
          query: { type: "sessionExpired" },
        });
        return;
      } else {
        this.$router.push({
          path: "/message",
          query: { type: "errorCheckingOut" },
        });
        return;
      }
    }

    this.setUpStripeJsInstance(
      paymentPageData.data.stripe.publishableKey,
      paymentPageData.data.stripe.account
    );

    await this.$nextTick();
    this.pageDataFetched = true;
    analyticsTrack("Page Viewed", {"accountId": this.accountId, "opptyId": this.opptyId, "version": this.applicationVersion, "phase": this.typeOfRenewal, "source": this.productSource, "page_name": "Payment"});
    
  },
};
</script>

<style scoped>
#payment-page {
  width: 100%;
  height: 100%;
  font-family: "Nitti Grotesk", sans-serif;
  font-style: normal;
  font-weight: normal;
  margin: 0 auto;
}

#payment-page ::v-deep label[for="company-name"] {
  display: none !important;
}

#body {
  max-width: 1080px;
  min-width: 320px;
  margin: 0 auto;
}

#ssl-notice {
  margin-top: 5px;
  margin-bottom: 15px;
}

.margin-top-20 {
  margin-top: 20px;
}

.margin-top-50 {
  margin-top: 50px;
}

.margin-bottom-20 {
  margin-bottom: 20px;
}

.margin-bottom-50 {
  margin-bottom: 50px;
}

.margin-bottom-100 {
  margin-bottom: 100px;
}

.black {
  color: black;
}

.bold {
  font-weight: bold;
}

#errors {
  margin-bottom: 10px;
}

.center-vertically {
  margin: auto;
}

#bacs-payment-details {
  list-style-type: none;
}

@media only screen and (max-width: 320px) {
  #ssl-notice {
    font-size: 11px;
  }
}

@media only screen and (min-width: 321px) and (max-width: 735px) {
  #ssl-notice {
    font-size: 14px;
  }
}

.mandate {
  padding-right: 11px;
  padding-left: 11px;
  max-width: 96%;
}

.mandate-h3 {
  font-size: 18px;
}

.mandate-h5 {
  font-size: 16px;
}

.b-width {
  max-width: 95%;
  margin: 0 auto;
  margin-top: 30px;
  margin-bottom: 40px;
}

.payment-details-header {
  font-weight: 500;
  font-size: 26px;
  padding-bottom: 8px;
  padding-left: 0;
  border-left: 0px solid;
  border-right: 0px solid;
  border-top: 0px solid;
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 20px;
  margin-top: 40px;
}

.email-verification {
  margin-left: 11px;
  margin-top: -20px;
  margin-bottom: 30px;
}
.default-card {
  padding: 20px;
  border-radius: 2px;
  align-items: center;
  padding: 18px;
  border: 0.50px #A9B7C0 solid;
}
.default_note {
  margin-top: 10px;
  margin-bottom: 35px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
}
.default_label {
  margin: 0;
  display: flex;
  align-items: center;
}
.default_checkbox {
  border-radius: 4px;
  border: 0.50px #C1C9D1 solid;
  margin: 0;
  margin-right: 15px;
}
.default_payment_expiration {
  font-size: 18px;
  color: #333;
}
.default-payment-info {
  font-size: 18px;
  font-weight: 700;
  padding-right: 20px;
  color: #333;
}
.display-card {
  height: 60px;
  width: fit-content;
  max-width: 95%;
  padding: 20px;
  border-radius: 2px;
  border: 1px solid rgb(231, 231, 231);
  margin-left: 11px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 40px;
  box-shadow: 1px 1px 5px rgb(233, 233, 233);
}

.display-card-text {
  margin: 0;
  width: auto;
}

.display-card-icon {
  margin-left: 20px;
  color: green;
  margin-top: 6px;
}

.change-bank {
  text-align: center;
  margin-top: -20px;
  margin-bottom: 40px;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
}

.ach-explanation-wrapper {
  padding: 15px;
  background-color: #f2f7ff;
  border-radius: 5px;
  max-width: 96%;
}

.ach-explanation-wrapper hr {
  background-color: lightgray;
  margin-top: 5px;
}

.pending {
  color: #f5a300;
}
.cc-notice-wrapper {
  padding: 15px;
  background-color: #f2f7ff;
  border-radius: 5px;
  margin-bottom: 20px;
}
</style>
